import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useMemo, useState } from 'react'

import DeleteItemModalWrapper from '../../components/Modals/DeleteItemModalWrapper'
import DeleteItemModal from '../../components/Modals/DeleteItemModal'

import useDeleteAction from './index'

import { clearAd, clearDeleteAd, deleteAd, getAd } from '../../../modules/actions/ads'
import {
  adDeleteErrorSelector,
  adDeleteIsLoadingSelector,
  adDeleteItemSelector,
  adSelector,
  adWasLoadedSelector
} from '../../../modules/selectors/ads'

export const useDeleteAdAction = ({ deleteCombinedAd = false } = {}) => {
  const dispatch = useDispatch()

  const ad = useSelector(adSelector)
  const adWasLoaded = useSelector(adWasLoadedSelector)

  const [isInternalAdDeletion, setIsInternalAdDeletion] = useState(false)

  const deleteAdHandler = useCallback(
    params => {
      dispatch(
        deleteAd(params, params.platform, {
          deleteCombinedAd,
          ...(isInternalAdDeletion && { deleteType: 'deleteInternalAd' })
        })
      )
    },
    [dispatch, deleteCombinedAd, isInternalAdDeletion]
  )

  const clearDeleteAdHandler = useCallback(() => {
    dispatch(clearDeleteAd())
  }, [dispatch])

  const {
    handleDelete,
    handleDeleteRequest,
    handleCloseDeleteModal,
    isDeleteModalOpened,
    deleteItemIsLoading,
    dataWaitingForDelete
  } = useDeleteAction({
    onDelete: deleteAdHandler,
    onClearDelete: clearDeleteAdHandler,
    deletedItemSelector: adDeleteItemSelector,
    deleteItemErrorSelector: adDeleteErrorSelector,
    deleteItemIsLoadingSelector: adDeleteIsLoadingSelector
  })

  const adListItem = dataWaitingForDelete?.item
  // We don't need to fetch ad and check if it has impressions if it's internal ad
  const shouldCheckAbilityToDelete = !adListItem?.internal

  const handleDeleteAdRequest = useCallback(
    params => {
      const { item: adListItem } = params
      setIsInternalAdDeletion(adListItem.internal)
      handleDeleteRequest(params)
    },
    [handleDeleteRequest]
  )

  const getListItemHandler = useCallback(() => {
    const { item = {}, platform: selectedPlatform = '', account: selectedAdAccountId = '' } = dataWaitingForDelete
    const isInternalAd = item.internal

    dispatch(
      getAd(
        {
          return_impressions: true, // todo PBR-1221 check if this param works when BE done
          account: selectedAdAccountId,
          id: item.id,
          ...(isInternalAd && { internal: true })
        },
        selectedPlatform
      )
    )
  }, [dispatch, dataWaitingForDelete])

  const clearHandler = useCallback(() => {
    dispatch(clearAd())
  }, [dispatch])

  const Modal = useMemo(() => {
    return (
      <DeleteItemModal
        handleDelete={handleDelete}
        handleCloseDeleteModal={handleCloseDeleteModal}
        isDeleteModalOpened={isDeleteModalOpened}
        deleteItemIsLoading={deleteItemIsLoading}
        deleteModalMessage="This cannot be undone."
        // props for archive button
        // archiveProps={{
        //   updateItemAction: updateLineItem,
        //   updatedItemSelector: lineItemUpdateSelector,
        //   updateItemErrorSelector: lineItemUpdateErrorSelector,
        //   updateItemIsLoadingSelector: lineItemUpdateIsLoadingSelector,
        //   clearSubmitAction: clearUpdateLineItem,
        //   dataWaitingForDelete
        // }}
      />
    )
  }, [handleDelete, handleCloseDeleteModal, isDeleteModalOpened, deleteItemIsLoading])

  return useMemo(() => {
    return {
      handleDeleteRequest: handleDeleteAdRequest,
      DeleteAdModal: shouldCheckAbilityToDelete ? (
        <DeleteItemModalWrapper
          isDeleteModalOpened={isDeleteModalOpened}
          handleCloseDeleteModal={handleCloseDeleteModal}
          getListItemHandler={getListItemHandler}
          clearHandler={clearHandler}
          loadedItem={ad}
          itemWasLoaded={adWasLoaded}
        >
          {Modal}
        </DeleteItemModalWrapper>
      ) : (
        Modal
      )
    }
  }, [
    handleDeleteAdRequest,
    isDeleteModalOpened,
    handleCloseDeleteModal,
    ad,
    adWasLoaded,
    getListItemHandler,
    clearHandler,
    shouldCheckAbilityToDelete,
    Modal
  ])
}
